import ErrorIcon from '@mui/icons-material/Error';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import { SxProps, useTheme } from '@mui/material/styles';
import { useController } from 'react-hook-form';

import { ERROR } from 'reservations/brand/constants';

type HookFormTextFieldProps = {
  name: string;
  dataTestId?: string;
  handleOnBlur?: (value: string) => void;
  autoFocus?: boolean;
  sxOverride?: SxProps;
  labelSxOverride?: SxProps;
  errorColorOverride?: SxProps;
} & TextFieldProps;

function HookFormTextField({
  name,
  label,
  dataTestId,
  placeholder = '',
  rows = 1,
  required = false,
  multiline = false,
  autoFocus = false,
  sxOverride = {},
  labelSxOverride = {},
  errorColorOverride = {},
  handleOnBlur
}: HookFormTextFieldProps) {
  const {
    field: { onChange, onBlur: formOnBlur, value, ref },
    formState: { errors }
  } = useController({
    name,
    rules: {
      required
    }
  });
  const theme = useTheme();
  let additionalProps = {};

  if (multiline) {
    additionalProps = {
      rows,
      multiline: true
    };
  }

  const onBlur = () => {
    if (handleOnBlur) {
      handleOnBlur(value);
    }
    // call the onBlur func from react-hook-form
    formOnBlur();
  };

  return (
    <TextField
      fullWidth
      data-testid={dataTestId}
      required={required}
      placeholder={placeholder}
      label={label}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      value={value}
      inputRef={ref}
      error={!!errors?.[name]?.message}
      helperText={(errors?.[name]?.message ?? null) as React.ReactNode}
      sx={theme => ({
        '& .Mui-focused': { color: theme.typography.body1 },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: ERROR }
      })}
      InputLabelProps={{
        sx: {
          ...labelSxOverride
        }
      }}
      InputProps={{
        endAdornment:
          // istanbul ignore next
          !!errors?.[name]?.message ? (
            <InputAdornment position="end">
              <ErrorIcon
                sx={{ color: (errorColorOverride as any)?.color || 'error' }}
              />
            </InputAdornment>
          ) : null,
        autoFocus,
        sx: {
          color: theme.typography.body1,
          padding: '0px',
          ...sxOverride
        }
      }}
      {...additionalProps}
    />
  );
}

export default HookFormTextField;
